/**
 * 기능 ID : COMVRF002-10MS, COMVRF002-20MS
 * ASIS : [전화요청] addCert.js line 1052 ~ 1145 , MA3CMMAAT001_201S
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppBridge, useGlobalErrorStore, useThrottle } from 'scbk-services';
import { Dialog } from 'scbk-shared-module';
import { BottomButtons } from 'scbk-ui-web';
import { useAutVrfAddApplyARS } from '../hooks/api/autVrfAdditionalHooks';
import { useAdditionalStore } from '../stores/useAdditionalStore';
import { AutVrfAddApplyARSRequest } from '../types/api/autVrfAddApplyARS';
import AdditionalARSStep1Component from './AdditionalARSStep1Component';
import AdditionalARSStep2Component from './AdditionalARSStep2Component';

interface Props {
  onComplete?: () => void;
  transType?: string;
  isLoading?: boolean;
}

const AdditionalARSComponent: React.FC<Props> = ({ onComplete, transType, isLoading = false }) => {
  // 다국어 처리
  const { t } = useTranslation();

  const { doNProgBackgroundService } = useAppBridge();

  const { subStep, setSubStep, selectedARSTelNo, activateInfo } = useAdditionalStore();

  const { request: applyARS } = useAutVrfAddApplyARS({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const throttleClick = useThrottle(() => {
    execApplyARS();
  }, 1000);

  const execApplyARS = useCallback(async () => {
    const request: AutVrfAddApplyARSRequest = {
      transType: transType ?? '',
      authTelNo: selectedARSTelNo?.telNo ?? '',
      account: 'SCFBIB',
      wasTranNo: activateInfo?.wasTranNo ?? '',
    };

    doNProgBackgroundService('1');

    const result = await applyARS(request);

    doNProgBackgroundService('2');

    if (result?.resultCode === '0000') {
      const isOk = await Dialog.alert({ content: t('AdditionalComponent.082', '인증되었어요.') });

      if (isOk) {
        onComplete?.();
      }
    } else {
      const errorMap: Record<string, { code: string; message: string }> = {
        '4003': {
          code: 'AdditionalComponent.083',
          message: '입력시간이 초과되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4011': {
          code: 'AdditionalComponent.084',
          message:
            '현재 고객님께서 통화중임으로 인증이 취소되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4012': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4014': {
          code: 'AdditionalComponent.083',
          message: '입력시간이 초과되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4017': {
          code: 'AdditionalComponent.086',
          message: '입력하신 인증번호가 일치하지 않습니다.',
        },
        '4018': {
          code: 'AdditionalComponent.087',
          message: '고객님과 전화연결이 중단되었습니다. 다시 인증요청 하시기 바랍니다.',
        },
        '4019': {
          code: 'AdditionalComponent.088',
          message:
            '현재 고객님께서 통화중이시거나, 음성사서함으로 연결되어 인증이 취소되었습니다. 다시 인증요청 하시기 바랍니다.',
        },
        '4021': {
          code: 'AdditionalComponent.089',
          message: '통신사 전화량이 많아 연결이 원활하지 않습니다. 다시 인증요청하시기 바랍니다.',
        },
        '4022': {
          code: 'AdditionalComponent.090',
          message: '통신사 시스템 장애로 연결이 되지 않습니다. 잠시 후 다시 거래하시 기 바랍니다.',
        },
        '4101': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4103': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4104': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4105': {
          code: 'AdditionalComponent.089',
          message: '통신사 전화량이 많아 연결이 원활하지 않습니다. 다시 인증요청하시기 바랍니다.',
        },
        default: {
          code: 'AdditionalComponent.091',
          message: '통신연결이 원활하지 않습니다. 잠시 후 다시 거래하시기 바랍니다.',
        },
      };

      const errorCode = result?.resultCode ?? 'default';

      const { code, message } = errorMap[errorCode] ?? errorMap.default;

      await Dialog.alert({
        content: t(code, message),
        onClick: () => {
          setSubStep(0);
        },
      });
    }
  }, [selectedARSTelNo, activateInfo]);

  const btnName =
    subStep == 0 ? t('AdditionalComponent.055', '다음') : t('AdditionalComponent.040', '전화요청');

  return (
    <>
      {subStep == 0 && <AdditionalARSStep1Component />}

      {subStep == 1 && <AdditionalARSStep2Component />}

      <BottomButtons
        buttons={[
          {
            label: btnName,
            onClick: () => {
              if (subStep == 0) {
                if (!selectedARSTelNo) {
                  Dialog.alert({
                    content: t('AdditionalComponent.106', '전화번호를 선택해 주세요.'),
                  });
                  return;
                }

                setSubStep((prev) => {
                  console.log(prev);
                  return prev + 1;
                });
              } else {
                throttleClick();
              }
            },
            Variants: 'primary',
            disabled: isLoading,
          },
        ]}
      />
    </>
  );
};

export default AdditionalARSComponent;
